import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Grid } from '@/UI/Grid/Grid';
import NavigationDrawer from '@/components/Dashboard/NavigationDrawer';
import { DashboardMain } from '@/components/Dashboard/DashboardMain';
import { I18N_KEYS } from '@/language/keys';
import { DashboardHeader } from '@/components/DashboardHeader/DashboardHeader';
import { useDashboardFolders } from '@/components/Dashboard/useDashboardFolders';
import { useWorkspaceFolders } from '@/components/Dashboard/useWorkspaceFolders';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { FlexCol } from '@/UI/styles';
import { log } from '@/logger/logger';
import { SubscriptionType } from '@/types/shared';
import { DashboardSection, TOOLKIT_PATH } from '@/components/Dashboard/enums/shared';
import { useDashboardSection } from '@/components/Dashboard/hooks/useDashboardSection';
import { getDashboardSectionFromScope, type DashboardPage } from '@/components/Dashboard/utils/dashboardSection.utils';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { ReloadAlert } from '@/UI/ReloadAlert/ReloadAlert';
import { WelcomeModal } from '@/components/WelcomeModal/WelcomeModal';
import { useMobileHandoffActivation } from '@/hooks/useMobileHandoffActivation';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_MOBILE_SIGNUP_HANDOFF, FF_TOOLS_CATALOG } from '@/consts/featureFlags';
import { useGlobalStore } from '@/state/global.state';
import DashboardArchive from '@/components/Dashboard/Archive/ArchiveTab';
import { Toolkit } from '@/components/Toolkit/Toolkit';
import { useGetArchiveItems } from '@/queries/useArchive';
import { useGetSharedWithMe, useGetPublicRecipes, useCreateRecipe } from '@/queries/useRecipes';
import { FolderScope } from '@/enums/folder-scope.enum';
import type { User } from '@/types/auth.types';

const logger = log.getLogger('Dashboard');

const DRAWER_WIDTH = 240;

interface DashboardLocationState {
  dashboardSection?: DashboardSection;
  dashboardPage?: DashboardPage;
}

interface DashboardProps {
  user: User;
}

export function Dashboard({ user }: DashboardProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const { value: isMobileSignupHandoffEnabled } = useFeatureFlag(FF_MOBILE_SIGNUP_HANDOFF);
  const { value: isToolsCatalogEnabled } = useFeatureFlag(FF_TOOLS_CATALOG);
  const areTabsEnabled = useToolTabsEnabled();
  const { folderId } = useParams<{ folderId?: string }>();
  const location = useLocation();
  const locationState = useMemo(() => (location.state ?? {}) as DashboardLocationState, [location.state]);
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [, setIsClosing] = useState(false);

  const { mutateAsync: createRecipe } = useCreateRecipe();

  const {
    data: { sharedWithMe: sharedWithCurrentUser = [], sharedApps = [] } = {},
    isError: sharedWithMeError,
    refetch: refetchSharedWithMe,
  } = useGetSharedWithMe();
  const { data: showCaseRecipes = [] } = useGetPublicRecipes();

  const {
    recipes: personalRecipes,
    folders: personalFolders,
    navigateToFolder: navigateToPersonalFolder,
    currentFolderId: currentPersonalFolderId,
    folderPath: personalFolderPath,
    isLoading: personalFoldersLoading,
    isError: personalFoldersError,
    refetch: refetchPersonalFolders,
  } = useDashboardFolders();

  const {
    recipes: workspaceRecipes,
    folders: workspaceFolders,
    navigateToFolder: navigateToWorkspaceFolder,
    currentFolderId: currentWorkspaceFolderId,
    folderPath: workspaceFolderPath,
    isLoading: workspaceFoldersLoading,
    isError: workspaceFoldersError,
    refetch: refetchWorkspaceFolders,
  } = useWorkspaceFolders();

  const { currentSelection, setCurrentSelection, currentPage, setCurrentPage } = useDashboardSection();
  const { isLoading: archiveLoading } = useGetArchiveItems(currentSelection === DashboardSection.Archive);

  useEffect(() => {
    const { dashboardSection: stateSection, dashboardPage: statePage } = locationState;

    if (statePage) {
      setCurrentPage(statePage);
    }

    if (folderId) {
      const isInWorkspaceFolders = workspaceFolders.some((f) => f.id === folderId);
      const isInPersonalFolders = personalFolders.some((f) => f.id === folderId);

      if (isInWorkspaceFolders) {
        navigateToWorkspaceFolder(folderId);
        setCurrentSelection(DashboardSection.WorkspaceFiles);
      } else if (isInPersonalFolders) {
        navigateToPersonalFolder(folderId);
        setCurrentSelection(DashboardSection.RecentRecipes);
      }
    } else {
      navigateToPersonalFolder(null);
      navigateToWorkspaceFolder(null);

      // Root-level recipes have no folderId, so we rely on the scope passed via
      // router state to select the correct dashboard section (e.g. Workspace Files).
      if (stateSection) {
        setCurrentSelection(stateSection);
      }
    }

    if (stateSection || statePage) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    folderId,
    location.pathname,
    locationState,
    navigate,
    navigateToPersonalFolder,
    navigateToWorkspaceFolder,
    setCurrentPage,
    setCurrentSelection,
    workspaceFolders,
    personalFolders,
  ]);

  const navigateToDashboardScope = useCallback(
    (scope: FolderScope, targetFolderId?: string | null) => {
      setCurrentSelection(getDashboardSectionFromScope(scope));
      navigate(targetFolderId ? `/folders/${targetFolderId}` : '/');
    },
    [navigate, setCurrentSelection],
  );

  const handleFolderNavigate = useCallback(
    (targetFolderId?: string | null) => {
      navigateToDashboardScope(FolderScope.PERSONAL, targetFolderId);
    },
    [navigateToDashboardScope],
  );

  const handleWorkspaceFolderNavigate = useCallback(
    (targetFolderId?: string | null) => {
      navigateToDashboardScope(FolderScope.WORKSPACE, targetFolderId);
    },
    [navigateToDashboardScope],
  );

  const handleCreateRecipe = useCallback(async () => {
    track('created_new_flow', { source: 'dashboard' }, TrackTypeEnum.BI);
    // Workspace workflows are not personal files. Shared has nothing of the caller's to add to, so
    // that tab still creates a private workflow, which opens on its own.
    const createsInWorkspace = areTabsEnabled && currentSelection === DashboardSection.WorkspaceFiles;
    try {
      await createRecipe(
        createsInWorkspace ? { parentId: currentWorkspaceFolderId, scope: FolderScope.WORKSPACE } : undefined,
      );
    } catch (error) {
      logger.error('error creating recipe', error);
    }
  }, [areTabsEnabled, createRecipe, currentSelection, currentWorkspaceFolderId, track]);

  const handleMobileHandoff = useCallback(async () => {
    try {
      await createRecipe();
      track('created_new_flow', { source: 'mobile_handoff' }, TrackTypeEnum.BI);
    } catch (error) {
      logger.error('error creating recipe from mobile handoff', error);
      updateSnackbarData({
        text: t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.ACTIVATION_ERROR),
        isOpen: true,
        severity: 'error',
        icon: null,
        duration: 10000,
      });
      throw error;
    }
  }, [createRecipe, t, track, updateSnackbarData]);

  useMobileHandoffActivation(handleMobileHandoff, isMobileSignupHandoffEnabled === true);

  const hasTeamOrEnterpriseSubscription = useMemo(() => {
    return (
      user.activeWorkspace.subscription.type === SubscriptionType.Team ||
      user.activeWorkspace.subscription.type === SubscriptionType.Enterprise
    );
  }, [user.activeWorkspace.subscription.type]);

  const handleSectionSelectionChange = useCallback(
    (selection: DashboardSection) => {
      if (selection === DashboardSection.Toolkit) {
        navigate(TOOLKIT_PATH);
      } else {
        setCurrentSelection(selection);
        // Clear the folder ID from URL when changing sections
        navigate('/');
      }

      // Also clear the folder navigation state
      if (currentPersonalFolderId) {
        navigateToPersonalFolder(null);
      }
      if (currentWorkspaceFolderId) {
        navigateToWorkspaceFolder(null);
      }
    },
    [
      navigate,
      currentPersonalFolderId,
      currentWorkspaceFolderId,
      navigateToPersonalFolder,
      navigateToWorkspaceFolder,
      setCurrentSelection,
    ],
  );

  const handleReloadPersonalFolders = useCallback(() => {
    void refetchPersonalFolders();
  }, [refetchPersonalFolders]);

  const handleReloadSharedWithMe = useCallback(() => {
    void refetchSharedWithMe();
  }, [refetchSharedWithMe]);

  const handleReloadWorkspaceFolders = useCallback(() => {
    void refetchWorkspaceFolders();
  }, [refetchWorkspaceFolders]);

  const pageTitle =
    currentPage === 'tool' ? t(I18N_KEYS.DASHBOARD.PAGES.TOOLS.TITLE) : t(I18N_KEYS.DASHBOARD.PAGES.WORKFLOWS.TITLE);
  const kindPageProps = useMemo(
    () =>
      areTabsEnabled
        ? {
            page: currentPage,
            showWorkspaceScope: hasTeamOrEnterpriseSubscription,
            title: pageTitle,
            onPageChange: setCurrentPage,
          }
        : {},
    [areTabsEnabled, currentPage, hasTeamOrEnterpriseSubscription, pageTitle, setCurrentPage],
  );

  const isDashboardLoading =
    personalFoldersLoading ||
    (currentSelection === DashboardSection.WorkspaceFiles && workspaceFoldersLoading) ||
    (currentSelection === DashboardSection.Archive && archiveLoading);

  const dashboardSections = useMemo(
    () => ({
      [DashboardSection.RecentRecipes]: (
        <DashboardMain
          recipes={personalRecipes}
          title={t(I18N_KEYS.DASHBOARD.PAGES.FILES.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.RecentRecipes}
          showChangeNameButton={hasTeamOrEnterpriseSubscription}
          onSectionChange={handleSectionSelectionChange}
          onFolderNavigate={handleFolderNavigate}
          currentFolderId={currentPersonalFolderId}
          folders={personalFolders}
          folderPath={personalFolderPath}
          isError={personalFoldersError}
          onReload={handleReloadPersonalFolders}
          {...kindPageProps}
        />
      ),
      [DashboardSection.SharedWithMe]: (
        <DashboardMain
          recipes={sharedWithCurrentUser}
          title={t(I18N_KEYS.DASHBOARD.PAGES.SHARED_WITH_ME.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.SharedWithMe}
          onSectionChange={handleSectionSelectionChange}
          isError={sharedWithMeError}
          onReload={handleReloadSharedWithMe}
          {...kindPageProps}
        />
      ),
      [DashboardSection.WorkspaceFiles]: (
        <DashboardMain
          recipes={workspaceRecipes}
          title={t(I18N_KEYS.DASHBOARD.PAGES.WORKSPACE_FILES.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.WorkspaceFiles}
          onSectionChange={handleSectionSelectionChange}
          onFolderNavigate={handleWorkspaceFolderNavigate}
          currentFolderId={currentWorkspaceFolderId}
          folders={workspaceFolders}
          folderPath={workspaceFolderPath}
          isError={workspaceFoldersError}
          onReload={handleReloadWorkspaceFolders}
          {...kindPageProps}
        />
      ),
      // Only reachable while the tabs are off, where the nav still offers it — with them on, one
      // section holds both kinds and this one has nothing of its own to show.
      [DashboardSection.SharedApps]: (
        <DashboardMain
          recipes={sharedApps}
          title={t(I18N_KEYS.DASHBOARD.PAGES.SHARED_APPS.TITLE)}
          showCase={showCaseRecipes}
          section={DashboardSection.SharedApps}
          isError={sharedWithMeError}
          onReload={handleReloadSharedWithMe}
        />
      ),
      [DashboardSection.Toolkit]: <Toolkit />,
      [DashboardSection.Archive]: <DashboardArchive />,
    }),
    [
      personalRecipes,
      sharedApps,
      showCaseRecipes,
      t,
      workspaceRecipes,
      sharedWithCurrentUser,
      handleFolderNavigate,
      handleWorkspaceFolderNavigate,
      hasTeamOrEnterpriseSubscription,
      handleSectionSelectionChange,
      currentPersonalFolderId,
      currentWorkspaceFolderId,
      personalFolders,
      personalFolderPath,
      personalFoldersError,
      handleReloadPersonalFolders,
      workspaceFolders,
      workspaceFolderPath,
      workspaceFoldersError,
      handleReloadWorkspaceFolders,
      sharedWithMeError,
      handleReloadSharedWithMe,
      kindPageProps,
    ],
  );

  if (isDashboardLoading) {
    return <PageLoader />;
  }

  // `undefined` means PostHog has not answered yet. Treating it as off would send an allowed person
  // who opened a /toolkit link back to the dashboard on a slow load.
  const isToolkit = currentSelection === DashboardSection.Toolkit;

  if (isToolkit && isToolsCatalogEnabled !== true) {
    return isToolsCatalogEnabled === false ? <Navigate to="/" replace /> : <PageLoader />;
  }

  return (
    <>
      <Grid container sx={{ position: 'relative', height: '100%' }} data-testid="dashboard-container">
        <Grid size="auto">
          <NavigationDrawer
            hasWorkspaceFiles={workspaceRecipes.length > 0 || workspaceFolders.length > 0}
            showWorkspaceFilesSection={hasTeamOrEnterpriseSubscription}
            drawerWidth={DRAWER_WIDTH}
            mobileOpen={mobileOpen}
            onSectionChange={handleSectionSelectionChange}
            onPageChange={setCurrentPage}
            selectedPage={currentPage}
            selectedSection={currentSelection}
            setIsClosing={setIsClosing}
            setMobileOpen={setMobileOpen}
            user={user}
          />
        </Grid>

        <Grid size="grow">
          <FlexCol sx={{ height: '100%' }}>
            <FlexCol
              sx={{
                // The Toolkit opens with its own full-width banner, so it starts at the top with no header.
                pt: isToolkit ? 0 : 4,
                px: 8,
                // The 32px under the org name is the gallery's own top padding. A column gap here
                // also sat above Tools, which has no gallery, and stacked with the title row's margin.
                height: '100%',
              }}
            >
              {isToolkit ? null : (
                <DashboardHeader
                  createRecipe={handleCreateRecipe}
                  isShowCreateRecipeButton={
                    areTabsEnabled ? currentPage === 'workflow' : currentSelection === DashboardSection.RecentRecipes
                  }
                />
              )}
              {dashboardSections[currentSelection]}
            </FlexCol>
          </FlexCol>
        </Grid>
      </Grid>
      <ReloadAlert />
      <WelcomeModal />
    </>
  );
}
